/**
 * Validates prescription data quality and completeness
 * @param {Object} data - Prescription data with scan_results and products
 * @returns {Object} - { isValid, quality, message, details }
 */
export const validatePrescriptionData = (data) => {
    // Check if we have the basic structure
    if (!data || !data.scan_results || !data.products) {
        return {
            isValid: false,
            quality: 'failed',
            message: 'Invalid prescription data structure'
        };
    }

    const scanResults = data.scan_results;
    const products = data.products || [];

    // Score for prescription details
    let prescriptionScore = 0;
    const prescriptionChecks = {
        hasValidName: scanResults.name &&
                     scanResults.name.trim() !== '' &&
                     !scanResults.name.toLowerCase().includes('strasse') &&
                     !scanResults.name.toLowerCase().includes('street') &&
                     scanResults.name.length > 3,
        hasValidSVNR: scanResults.svnr &&
                     scanResults.svnr.trim() !== '' &&
                     /\d{4}/.test(scanResults.svnr), // Should contain at least 4 digits
        hasValidRezId: scanResults.rez_id &&
                      scanResults.rez_id.trim() !== '',
        hasValidAddress: scanResults.address_1 &&
                       scanResults.address_1.trim() !== '' &&
                       scanResults.address_1.toLowerCase() !== 'fail',
        hasIssuedDate: scanResults.issued_date &&
                      scanResults.issued_date.trim() !== '',
        hasExpiredDate: scanResults.expired_date &&
                       scanResults.expired_date.trim() !== '',
        hasEMedId: scanResults.e_med_id &&
                  scanResults.e_med_id.trim() !== ''
    };

    // Count prescription detail completeness
    if (prescriptionChecks.hasValidName) prescriptionScore++;
    if (prescriptionChecks.hasValidSVNR) prescriptionScore++;
    if (prescriptionChecks.hasValidRezId) prescriptionScore++;
    if (prescriptionChecks.hasValidAddress) prescriptionScore++;
    if (prescriptionChecks.hasIssuedDate) prescriptionScore++;
    if (prescriptionChecks.hasExpiredDate) prescriptionScore++;
    if (prescriptionChecks.hasEMedId) prescriptionScore++;

    // Score for products
    let productScore = 0;
    let productsWithIds = 0;
    let validProducts = 0;

    products.forEach(product => {
        // Check if product has valid IDs
        if (product.ids && Array.isArray(product.ids) && product.ids.length > 0) {
            // Check if at least one ID is not null
            const hasValidId = product.ids.some(id => id && id[0] !== null);
            if (hasValidId) {
                productsWithIds++;
            }
        }

        // Check if product has valid label and quantity
        if (product.label && product.label.trim() !== '' &&
            product.label.length > 1 && // Not just single character
            product.op && product.op.trim() !== '') {
            validProducts++;
        }
    });

    // Calculate product score
    if (productsWithIds > 0) productScore += 2;
    if (productsWithIds >= 2) productScore += 1;
    if (validProducts > 0) productScore += 1;
    if (validProducts >= 2) productScore += 1;

    // Determine overall quality
    const totalScore = prescriptionScore + productScore;

    // Critical requirements for proceeding
    const hasCriticalInfo = prescriptionChecks.hasValidName &&
                            prescriptionChecks.hasValidSVNR &&
                            prescriptionChecks.hasValidRezId;

    // Determine quality level and message
    if (totalScore >= 8 && hasCriticalInfo && productsWithIds > 0) {
        return {
            isValid: true,
            quality: 'good',
            message: 'Prescription scanned successfully'
        };
    } else if (totalScore >= 5 && (hasCriticalInfo || productsWithIds > 0)) {
        return {
            isValid: false,
            quality: 'partial',
            message: 'Prescription partially detected. Please try scanning again with better lighting and angle for more accurate results.',
            details: {
                missingCritical: !hasCriticalInfo,
                lowProductMatches: productsWithIds === 0
            }
        };
    } else {
        return {
            isValid: false,
            quality: 'failed',
            message: 'Could not detect prescription details. Please ensure the prescription is clearly visible and try again.',
            details: {
                prescriptionScore,
                productScore,
                hasName: prescriptionChecks.hasValidName,
                hasSVNR: prescriptionChecks.hasValidSVNR,
                hasRezId: prescriptionChecks.hasValidRezId
            }
        };
    }
};
